import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

// Shows a modal that arrived in a turbo frame, and -- crucially -- tears it
// down again when the frame's content is swapped.
//
// Bootstrap appends the backdrop to <body>, OUTSIDE the frame it manages, so
// a swap alone never removes it: a form re-rendered with validation errors
// (422 into the same frame) used to leave the old backdrop standing while the
// replacement modal added its own, and dismissing the replacement only took
// down the newer of the two. The page stayed dimmed and unclickable.
export default class extends Controller {
  static targets = ['modal']

  connect() {
    this.closeOnSuccess = this.closeOnSuccess.bind(this)
    this.modal = new bootstrap.Modal(this.modalTarget)
    this.modal.show()
    this.element.addEventListener('turbo:submit-end', this.closeOnSuccess)
  }

  disconnect() {
    this.element.removeEventListener('turbo:submit-end', this.closeOnSuccess)
    // Drops this instance's own backdrop along with the instance.
    this.modal?.dispose()
    this.modal = null
    this.clearStaleChrome()
  }

  // Whatever chrome is still standing with no modal on screen belongs to an
  // element that has already been swapped away -- including a backdrop left
  // mid-fade, since hide() strips .show before the transition ends.
  clearStaleChrome() {
    if (document.querySelector('.modal.show')) return

    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove())
    document.body.classList.remove('modal-open')
    document.body.style.removeProperty('overflow')
    document.body.style.removeProperty('padding-right')
  }

  // A form marked data-skip-modal-auto-close="true" (the same opt-out
  // turbo-frame-modal reads) re-renders inside the dialog instead: its
  // stream decides what changes, so the modal stays up.
  closeOnSuccess(event) {
    if (!event.detail.success) return
    if (event.target.dataset.skipModalAutoClose === 'true') return

    this.close()
  }

  open() {
    this.modal.show()
  }

  close(event) {
    this.modal?.hide()
  }
}
